/**
 * Smallest axis-aligned rectangle containing all non-null rects (bridges gaps when
 * moving between minimized slot and portaled icon row).
 *
 * @param {Array<{ left: number, right: number, top: number, bottom: number } | null | undefined>} rects
 * @returns {{ left: number, top: number, right: number, bottom: number } | null}
 */
export function unionBoundingRectForHotZone(rects) {
  if (!Array.isArray(rects)) {
    throw new Error('unionBoundingRectForHotZone: rects must be an array');
  }
  let left = Infinity;
  let top = Infinity;
  let right = -Infinity;
  let bottom = -Infinity;
  let any = false;
  for (let i = 0; i < rects.length; i += 1) {
    const r = rects[i];
    if (!r || !Number.isFinite(r.left) || !Number.isFinite(r.top) || !Number.isFinite(r.right) || !Number.isFinite(r.bottom)) {
      continue;
    }
    any = true;
    left = Math.min(left, r.left);
    top = Math.min(top, r.top);
    right = Math.max(right, r.right);
    bottom = Math.max(bottom, r.bottom);
  }
  if (!any) return null;
  return { left, top, right, bottom };
}

/**
 * Hit-test for desktop battlefield unit expand: keep the portaled expanded card
 * open only while the pointer is over the minimized slot and/or the ability/weapon
 * icon row on the expanded card.
 *
 * @param {number} clientX
 * @param {number} clientY
 * @param {Array<{ left: number, right: number, top: number, bottom: number } | null | undefined>} rects
 * @param {number} padPx
 * @returns {boolean}
 */
export function isInsideBattlefieldUnitExpandHotZone(clientX, clientY, rects, padPx) {
  if (!Number.isFinite(clientX) || !Number.isFinite(clientY)) {
    throw new Error('isInsideBattlefieldUnitExpandHotZone: client coordinates must be finite numbers');
  }
  if (typeof padPx !== 'number' || padPx < 0 || !Number.isFinite(padPx)) {
    throw new Error('isInsideBattlefieldUnitExpandHotZone: padPx must be a non-negative finite number');
  }
  if (!Array.isArray(rects)) {
    throw new Error('isInsideBattlefieldUnitExpandHotZone: rects must be an array');
  }
  for (let i = 0; i < rects.length; i += 1) {
    const rect = rects[i];
    if (!rect) continue;
    if (
      clientX >= rect.left - padPx &&
      clientX <= rect.right + padPx &&
      clientY >= rect.top - padPx &&
      clientY <= rect.bottom + padPx
    ) {
      return true;
    }
  }
  return false;
}

/**
 * Per-rect hit test plus padded union of all rects (cursor path between slot and portaled icons).
 *
 * @param {number} clientX
 * @param {number} clientY
 * @param {Array<{ left: number, right: number, top: number, bottom: number } | null | undefined>} rects
 * @param {number} padPx
 * @returns {boolean}
 */
export function isInsideBattlefieldUnitExpandHotZoneWithUnion(clientX, clientY, rects, padPx) {
  const parts = rects.filter((r) => r && Number.isFinite(r.left));
  const union = unionBoundingRectForHotZone(parts);
  const withUnion = union ? [...parts, union] : parts;
  return isInsideBattlefieldUnitExpandHotZone(clientX, clientY, withUnion, padPx);
}

/** Front/flank rows use negative marginLeft on idx > 0; lower indices must stack above when idle. */
export const OVERLAPPING_BATTLEFIELD_SLOT_COUNT = 5;

/**
 * @param {number} slotIndex - 0-based slot in the row
 * @param {boolean} isElevated - hovered, selected attacker, or pinned inspect
 * @param {number} [slotCount]
 * @param {boolean} [preferLeftSlotOnTop] - when true and idle, left slots stack above overlaps (slot 0 clickable); when false, use DOM order (stable while hovering)
 * @returns {number}
 */
export function getOverlappingBattlefieldSlotZIndex(
  slotIndex,
  isElevated,
  slotCount = OVERLAPPING_BATTLEFIELD_SLOT_COUNT,
  preferLeftSlotOnTop = true
) {
  if (isElevated) return 100;
  const n = Number(slotCount);
  const i = Number(slotIndex);
  if (!Number.isFinite(n) || n < 1 || !Number.isFinite(i) || i < 0 || i >= n) {
    throw new Error('getOverlappingBattlefieldSlotZIndex: invalid slot index or count');
  }
  return preferLeftSlotOnTop ? (n - i) : (i + 1);
}
